import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { Order } from '../types';
import SimpleFormModal from '../components/SimpleFormModal';
import Modal from '../components/Modal';
import './PurchaseHistory.css';

interface TrackingInfo {
  trackingNumber: string;
  carrier: string;
  shippedDate?: string;
  estimatedDeliveryDate?: string;
  deliveredDate?: string;
  status: string;
}

const PurchaseHistory: React.FC = () => {
  const { success, error: showError, warning } = useToast();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [returning, setReturning] = useState<boolean>(false);
  const [returnRequest, setReturnRequest] = useState<{ orderId: number; orderItemId: number; maxQuantity: number } | null>(null);
  const [trackingByOrder, setTrackingByOrder] = useState<Record<number, TrackingInfo>>({});
  const [reviewTarget, setReviewTarget] = useState<{ productId: number; productName: string } | null>(null);
  const [reviewRating, setReviewRating] = useState<number>(5);
  const [reviewComment, setReviewComment] = useState<string>('');
  const [submittingReview, setSubmittingReview] = useState<boolean>(false);

  const fetchTracking = useCallback(async (loadedOrders: Order[]): Promise<void> => {
    const shippedOrders = loadedOrders.filter(
      (o) => o.status === 'Shipped' || o.status === 'Delivered'
    );
    const results = await Promise.all(
      shippedOrders.map((o) =>
        api
          .get<TrackingInfo>(`/shipping/tracking/${o.id}`)
          .then((res) => ({ orderId: o.id, info: res.data }))
          .catch(() => null) // No shipping record yet; just don't show tracking
      )
    );
    const map: Record<number, TrackingInfo> = {};
    for (const result of results) {
      if (result?.info?.trackingNumber) {
        map[result.orderId] = result.info;
      }
    }
    setTrackingByOrder(map);
  }, []);

  const fetchOrders = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<Order[]>('/orders');
      setOrders(response.data);
      fetchTracking(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchTracking]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const openReviewModal = (productId: number, productName: string): void => {
    setReviewRating(5);
    setReviewComment('');
    setReviewTarget({ productId, productName });
  };

  const handleSubmitReview = async (): Promise<void> => {
    if (!reviewTarget) return;
    setSubmittingReview(true);
    try {
      await api.post('/reviews', {
        productId: reviewTarget.productId,
        rating: reviewRating,
        comment: reviewComment.trim() || null
      });
      success('Thanks for your review!');
      setReviewTarget(null);
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleRequestReturn = async (orderId: number, orderItemId: number, reason: string, quantity: number, description?: string): Promise<void> => {
    if (!reason.trim()) {
      warning('Return reason is required.');
      return;
    }
    setReturning(true);
    try {
      await api.post('/returns', {
        orderId,
        orderItemId,
        reason,
        quantity: quantity > 0 ? quantity : 1,
        description: description || undefined
      });

      success('Return request submitted successfully!');
      fetchOrders();
      setReturnRequest(null);
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to submit return request');
    } finally {
      setReturning(false);
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'delivered':
        return 'var(--color-success)';
      case 'processing':
      case 'shipped':
        return 'var(--color-teal)';
      case 'cancelled':
        return 'var(--color-danger)';
      default:
        return 'var(--text-secondary)';
    }
  };

  const canReturnItem = (order: Order): boolean => {
    const deliveredDate = order.updatedAt || order.createdAt;
    const daysSinceDelivery = (new Date().getTime() - new Date(deliveredDate).getTime()) / (1000 * 60 * 60 * 24);
    return order.status === 'Delivered' && daysSinceDelivery <= 30;
  };

  if (loading) {
    return <div className="loading">Loading purchase history...</div>;
  }

  return (
    <div className="purchase-history-page">
      <div className="container">
        <h1 className="page-title">Purchase History</h1>
        <p>
          Need to send something back? Read the{' '}
          <Link to="/returns-policy">returns policy</Link>, then request a return on a delivered item.
        </p>

        {orders.length === 0 ? (
          <div className="no-orders">
            <p>You haven't placed any orders yet.</p>
          </div>
        ) : (
          <div className="orders-list">
            {orders.map((order) => (
              <div key={order.id} className="order-card">
                <div className="order-header">
                  <div>
                    <h3>Order #{order.orderNumber || order.id}</h3>
                    <p className="order-date">
                      Placed on {new Date(order.createdAt).toLocaleDateString()}
                      {order.isGift && ' · Gift'}
                    </p>
                    {order.isGift && order.giftMessage && (
                      <p className="order-date">Gift message: {order.giftMessage}</p>
                    )}
                  </div>
                  <div className="order-status">
                    <span
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(order.status) }}
                    >
                      {order.status}
                    </span>
                  </div>
                </div>

                <div className="order-items">
                  {order.orderItems.map((item) => {
                    const variantOptions = item.productVariant?.options || {};
                    
                    return (
                      <div key={item.id} className="order-item">
                        <div className="order-item-image">
                          <img
                            src={item.product.croppedImageUrl || item.product.imageUrl || '/placeholder-image.svg'}
                            alt={item.product.name}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              if (!target.src.endsWith('/placeholder-image.svg')) {
                                target.src = '/placeholder-image.svg';
                              }
                            }}
                          />
                        </div>
                        <div className="order-item-details">
                          <h4>{item.product.name}</h4>
                          {item.product.sku && (
                            <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>SKU: {item.product.sku}</p>
                          )}
                          {item.productVariant?.sku && (
                            <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Variant SKU: {item.productVariant.sku}</p>
                          )}
                          {Object.keys(variantOptions).length > 0 && (
                            <div style={{ marginTop: '5px' }}>
                              {Object.entries(variantOptions).map(([key, value]) => (
                                <p key={key} style={{ fontSize: '14px', margin: '2px 0' }}>
                                  {key}: {value}
                                </p>
                              ))}
                            </div>
                          )}
                          {!item.productVariant && (
                            <>
                              {item.product.size && <p>Size: {item.product.size}</p>}
                              {item.product.material && <p>Material: {item.product.material}</p>}
                            </>
                          )}
                          <p>Quantity: {item.quantity}</p>
                          <p className="order-item-price">
                            ${item.totalPrice.toFixed(2)}
                          </p>
                          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            {order.status === 'Delivered' && (
                              <button
                                onClick={() => openReviewModal(item.productId, item.product.name)}
                                className="btn btn-secondary btn-small"
                                style={{ marginTop: '10px' }}
                              >
                                <i className="fas fa-star"></i> Leave a Review
                              </button>
                            )}
                            {canReturnItem(order) && (
                              <button
                                onClick={() => setReturnRequest({ orderId: order.id, orderItemId: item.id, maxQuantity: item.quantity })}
                                className="btn btn-secondary btn-small"
                                style={{ marginTop: '10px' }}
                              >
                                Request Return
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="order-footer">
                  <div className="order-total">
                    <strong>Total: ${order.totalAmount.toFixed(2)}</strong>
                    {order.discountAmount && order.discountAmount > 0 && (
                      <p style={{ fontSize: '14px', color: 'var(--color-success)', marginTop: '5px' }}>
                        Discount: -${order.discountAmount.toFixed(2)}
                        {order.couponCode && ` (${order.couponCode})`}
                      </p>
                    )}
                  </div>
                  {order.shippingAddress && (
                    <div className="shipping-info">
                      <p>
                        <strong>Shipping to:</strong> {order.shippingAddress}, {order.shippingCity},{' '}
                        {order.shippingState} {order.shippingZipCode}
                      </p>
                      {trackingByOrder[order.id] && (
                        <p style={{ marginTop: '5px' }}>
                          <strong>Tracking:</strong> {trackingByOrder[order.id].trackingNumber}{' '}
                          ({trackingByOrder[order.id].carrier})
                          {trackingByOrder[order.id].deliveredDate ? (
                            <> — delivered {new Date(trackingByOrder[order.id].deliveredDate!).toLocaleDateString()}</>
                          ) : trackingByOrder[order.id].estimatedDeliveryDate ? (
                            <> — estimated delivery {new Date(trackingByOrder[order.id].estimatedDeliveryDate!).toLocaleDateString()}</>
                          ) : null}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Modal
        isOpen={reviewTarget !== null}
        onClose={() => setReviewTarget(null)}
        title={`Review: ${reviewTarget?.productName || ''}`}
        size="small"
      >
        <div className="form-group">
          <label>Rating</label>
          <div style={{ display: 'flex', gap: '5px', fontSize: '24px' }}>
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setReviewRating(star)}
                aria-label={`${star} star${star > 1 ? 's' : ''}`}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '2px',
                  color: star <= reviewRating ? '#f5b301' : 'var(--text-secondary)'
                }}
              >
                <i className={star <= reviewRating ? 'fas fa-star' : 'far fa-star'}></i>
              </button>
            ))}
          </div>
        </div>
        <div className="form-group">
          <label>Comment (optional)</label>
          <textarea
            rows={4}
            value={reviewComment}
            onChange={(e) => setReviewComment(e.target.value)}
            placeholder="What did you think of this print?"
          />
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setReviewTarget(null)}
            disabled={submittingReview}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmitReview}
            disabled={submittingReview}
          >
            {submittingReview && <span className="btn-spinner" aria-hidden="true"></span>}
            Submit Review
          </button>
        </div>
      </Modal>
      <SimpleFormModal
        isOpen={returnRequest !== null}
        title="Request Return"
        fields={[
          {
            name: 'reason',
            label: 'Return Reason',
            type: 'select',
            required: true,
            placeholder: 'Select a reason',
            options: [
              { value: 'Defective', label: 'Damaged or defective' },
              { value: 'WrongItem', label: 'Wrong item received' },
              { value: 'NotAsDescribed', label: 'Not as described' },
              { value: 'ChangedMind', label: 'Changed my mind (10% restocking fee)' },
              { value: 'Other', label: 'Other' }
            ]
          },
          { name: 'description', label: 'Details', type: 'textarea', placeholder: 'Optional details that help us process the return.' },
          {
            name: 'quantity',
            label: 'Quantity',
            type: 'number',
            required: true,
            min: '1',
            max: returnRequest ? String(returnRequest.maxQuantity) : '1'
          }
        ]}
        initialValues={{ quantity: returnRequest ? String(returnRequest.maxQuantity) : '1' }}
        submitLabel="Submit Return"
        loading={returning}
        onSubmit={(values) => {
          if (returnRequest) {
            handleRequestReturn(
              returnRequest.orderId,
              returnRequest.orderItemId,
              values.reason || '',
              parseInt(values.quantity, 10) || 1,
              values.description
            );
          }
        }}
        onClose={() => setReturnRequest(null)}
      />
    </div>
  );
};

export default PurchaseHistory;

